import * as React from 'react';
import { Query } from 'react-apollo';

import AdminDistrictSisDashboardContainer from 'admin/rostering/containers/AdminDistrictSisDashboardContainer';
import AdminSchoolSisDashboard from 'admin/rostering/components/AdminSchoolSisDashboard';
import AdminSisSyncPicker from 'admin/rostering/components/AdminSisSyncPicker';
import ErrorReporter from 'app/components/ErrorReporter';
import LoadingIndicator from 'components/LoadingIndicator';

import ADMIN_SIS_DASHBOARD_QUERY from 'admin/rostering/queries/AdminSisDashboardQuery.gql';

import {
  AdminSisDashboard as AdminSchoolSisDashboard_Data,
  AdminSisDashboard_organizations as Organization,
  AdminSisDashboardVariables,
} from 'admin/rostering/queries/__generated__/AdminSisDashboard';

import { findMatching } from 'utils/arrayHelpers';

export type SyncMethods = 'None' | 'Clever' | 'SFTP' | null;

type Props = {
  orgId: number;
};

type State = {
  selectedSyncMethod: SyncMethods;
};

export function getCurrentSyncMethodForOrg(org: Organization): SyncMethods {
  if (!org.sisSyncOverview) {
    return null;
  }
  const { districtStatus } = org.sisSyncOverview;
  const cleverSynced = org.childOrganizations.some(
    childOrg => childOrg.sisSyncOverview?.syncMethod === 'Clever'
  );

  const awaitingFiles = districtStatus?.awaitingFiles ?? false;
  const hasSchoolMappings = districtStatus?.schoolMappings
    ? districtStatus.schoolMappings.length > 0
    : false;
  const hasUploadErrors = districtStatus?.uploadErrors
    ? districtStatus?.uploadErrors.length > 0
    : false;

  if (cleverSynced) {
    return 'Clever';
  } else if (awaitingFiles || hasSchoolMappings || hasUploadErrors) {
    return 'SFTP';
  } else {
    return 'None';
  }
}

export default class AdminSisDashboardContainer extends React.Component<Props, State> {
  state = {
    selectedSyncMethod: null,
  };

  handlePickSyncMethod = (selectedSyncMethod: SyncMethods) => {
    this.setState({ selectedSyncMethod });
  };

  render() {
    const { orgId } = this.props;

    return (
      <Query<AdminSchoolSisDashboard_Data, AdminSisDashboardVariables>
        query={ADMIN_SIS_DASHBOARD_QUERY}
        variables={{
          organizationIds: [orgId],
        }}>
        {({ data, error, loading }) => {
          const { selectedSyncMethod } = this.state;

          if (loading) {
            return <LoadingIndicator height={400} size="small" />;
          }

          if (error) {
            return (
              <ErrorReporter
                error={error || 'AdminSisDashboardContainer - graphql loading error'}
              />
            );
          }

          if (!data?.organizations) {
            return (
              <ErrorReporter error={error || 'AdminSisDashboardContainer - could not load data'} />
            );
          }

          const organization = findMatching(data.organizations, orgId, 'id');

          if (!organization) {
            console.error(
              'AdminSisDashboardContainer - expected the provided org uuid to be query a result'
            );
            return null;
          }

          const canSeeDashboard = organization.permissions.adminConsole.seeRosteringTab;

          if (!canSeeDashboard) {
            return (
              <ErrorReporter error={'SIS management page - non-paid accounts should not be here'}>
                <span data-test="non-paid">
                  Uh, oh. You should not be here, something weird happened.
                </span>
              </ErrorReporter>
            );
          }

          if (organization.type === 'District') {
            const currentSyncMethod = getCurrentSyncMethodForOrg(organization);
            const syncMethod = selectedSyncMethod || currentSyncMethod;

            if (syncMethod === 'None') {
              return <AdminSisSyncPicker pickSyncMethod={this.handlePickSyncMethod} />;
            } else if (syncMethod) {
              return <AdminDistrictSisDashboardContainer orgId={organization.id} syncMethod={syncMethod} />;
            } else {
              return null;
            }
          } else {
            // School
            return <AdminSchoolSisDashboard loading={loading} orgUuid={organization.uuid} />;
          }
        }}
      </Query>
    );
  }
}
